import React, { memo } from 'react'
import { Icon, Text, View, Poll } from '@/components'
import { theme } from '@/config'
import { Chat } from '@/config/packages'
import { MessagePin } from './MessagePin'
import { MessageAttachments } from './MessageAttachments'
import { MessageReactionsPreview } from './MessageReactionsPreview'
import { MessageReplies } from './MessageReplies'
import { MessageActionMenu } from './MessageActionMenu'
import { MessageText } from './MessageText'
import { QuotedMessagePreview } from './QuotedMessagePreview'
import { getReactionsTotalCount, isMessageDeleted } from '@packages/chat'
import { Channel } from 'stream-chat'

export type MessageBubbleProps = {
  message: any
  channel: Channel
  isMyMessage: boolean
  isDeleted: boolean
  isEdited: boolean
  isForwarded: boolean
  isThreadParent: boolean
  hasAudioAttachment: boolean
  isGroup: boolean
  firstGroupMessage: boolean
  userName: string
  isPoll: boolean
  threadList?: boolean
  styles: any
  hasTail: boolean
  onDelete: () => void
  onSelect: () => void
  onReply: () => void
  onCopy: () => void
  onStar: () => void
  onForward: () => void
  onSetReminder: () => void
  onEdit: () => void
}

const MessageBubbleInner = ({
  message,
  channel,
  isMyMessage,
  isDeleted,
  isEdited,
  isForwarded,
  isThreadParent,
  hasAudioAttachment,
  isGroup,
  firstGroupMessage,
  userName,
  isPoll,
  threadList,
  styles,
  onDelete,
  onSelect,
  onReply,
  onCopy,
  onStar,
  onForward,
  onSetReminder,
  onEdit,
}: MessageBubbleProps) => {
  const showPin = message?.pinned && !isDeleted && !(isThreadParent && !hasAudioAttachment)
  const showForwarded = isForwarded && !isDeleted && !(isThreadParent && !hasAudioAttachment)
  const showUserName = isGroup && !isMyMessage && firstGroupMessage

  return (
    <div
      css={[styles.messageBubble]}
      className={`message-bubble ${isMyMessage ? 'message-bubble--me' : ''}`}
    >
      {showPin && <MessagePin />}

      {isDeleted && (
        <View style={styles.deletedMessageWrapper}>
          <Icon name='slash-line' style={[styles.deletedIcon]} />
          <Text text={Chat.Constants.deletedMessage.text} css={[styles.messageText, styles.deletedMessageText]} />
        </View>
      )}

      {!isDeleted && (
        <>
          {showForwarded && (
            <View style={styles.forwardedIndicator}>
              <Icon name='share-06' style={styles.forwardedIcon} />
              <Text text='Forwarded' style={['c3', 'medium', styles.forwardedText]} />
            </View>
          )}

          {showUserName && (
            <Text
              text={userName}
              style={[styles.userName, message?.quoted_message && styles.userNameWithQuotedMessage]}
            />
          )}

          <QuotedMessagePreview message={message} styles={styles} />

          {isPoll && (
            <View style={styles.pollWrapper}>
              <Poll message={message} isThreadParent={isThreadParent} />
            </View>
          )}

          <MessageAttachments channel={channel} />

          <MessageText styles={styles} message={message} channel={channel} />

          <BubbleFooter message={message} styles={styles} isEdited={isEdited} />

          <MessageActionMenuWrapper
            message={message}
            threadList={threadList}
            onDelete={onDelete}
            onSelect={onSelect}
            onReply={onReply}
            onCopy={onCopy}
            onStar={onStar}
            onForward={onForward}
            onSetReminder={onSetReminder}
            onEdit={onEdit}
          />
        </>
      )}
    </div>
  )
}

const BubbleFooter = memo(({ styles, message, isEdited }: {
  styles: any
  message: any
  isEdited: boolean
}) => {
  const reactionCount = getReactionsTotalCount(message?.reaction_counts)
  const repliesCount = message?.reply_count || 0

  if (reactionCount === 0 && repliesCount === 0 && !isEdited) return null

  return (
    <View style={styles.bubbleFooter}>
      <MessageReactionsPreview />
      <MessageReplies />
      {isEdited && (
        <Text text='Edited' style={styles.editedText} />
      )}
    </View>
  )
}, (prev, next) => {
  return (
    prev.isEdited === next.isEdited &&
    prev.message?.latest_reactions === next.message?.latest_reactions &&
    prev.message?.reply_count === next.message?.reply_count
  )
})

const MessageActionMenuWrapper = memo(({ message, threadList, ...handlers }: any) => {
  const selectMode = Chat.Store.useSelectMode({ threadList }).selectModeActive

  if (selectMode) return null
  if (isMessageDeleted(message)) return null

  return <MessageActionMenu message={message} {...handlers} />
}, (prev, next) => {
  return (
    prev.message?.id === next.message?.id &&
    prev.message?.pinned === next.message?.pinned &&
    prev.message?.text === next.message?.text &&
    prev.threadList === next.threadList
  )
})

const areEqual = (prev: MessageBubbleProps, next: MessageBubbleProps) => {
  const { message: prevMessage } = prev
  const { message: nextMessage } = next

  if (prev.isMyMessage !== next.isMyMessage) return false
  if (prev.isDeleted !== next.isDeleted) return false
  if (prev.isEdited !== next.isEdited) return false
  if (prev.isForwarded !== next.isForwarded) return false
  if (prev.isThreadParent !== next.isThreadParent) return false
  if (prev.hasAudioAttachment !== next.hasAudioAttachment) return false
  if (prev.isGroup !== next.isGroup) return false
  if (prev.firstGroupMessage !== next.firstGroupMessage) return false
  if (prev.isPoll !== next.isPoll) return false
  if (prev.hasTail !== next.hasTail) return false

  if (prev.message?.id !== next.message?.id) return false
  if (prev.message?.text !== next.message?.text) return false
  if (prev.message?.pinned !== next.message?.pinned) return false
  if (prev.message?.latest_reactions !== next.message?.latest_reactions) return false
  if (prev.message?.reply_count !== next.message?.reply_count) return false
  if (prev.message?.quoted_message?.id !== next.message?.quoted_message?.id) return false
  if (prev.message?.quoted_message?.type !== next.message?.quoted_message?.type) return false
  if (prev.message?.quoted_message?.deleted_at !== next.message?.quoted_message?.deleted_at) return false
  if (prev.message?.quoted_message?.text !== next.message?.quoted_message?.text) return false

  const latestReactionsEqual =
    Array.isArray(prevMessage.latest_reactions) && Array.isArray(nextMessage.latest_reactions)
      ? prevMessage.latest_reactions.length === nextMessage.latest_reactions.length &&
      prevMessage.latest_reactions.every(
        ({ type }, index) => type === nextMessage.latest_reactions?.[index].type,
      )
      : prevMessage.latest_reactions === nextMessage.latest_reactions
  if (!latestReactionsEqual) {
    return false
  }

  return true
}

export const MessageBubble = memo(MessageBubbleInner, areEqual)
